import { useState } from 'react';
import api from '@/lib/api';
import { getDebugState } from '@/lib/telemetry';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CheckCircle2, Copy, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import type { PluginBuilderSession, VirtualFileSystem, GuidedFlow, BuildPlan } from '../types';

const MAX_DESCRIPTION_LENGTH = 5000;

// Cap log entries in the submitted report so a chatty preview iframe can't
// balloon the payload past the server bodyLimit. The most recent entries are
// the ones with diagnostic value.
const MAX_REPORTED_LOG_ENTRIES = 1000;

const SCOPE_OPTIONS = [
  { value: '1', label: 'Last exchange' },
  { value: '3', label: 'Last 3 exchanges' },
  { value: '5', label: 'Last 5 exchanges' },
  { value: '10', label: 'Last 10 exchanges' },
];

interface GuidedBuilderState {
  mode: string;
  phase: string;
  autoApprove: boolean;
}

interface PluginReportProblemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: PluginBuilderSession | null;
  files: VirtualFileSystem | null;
  logEntries: unknown[];
  guidedFlow: GuidedFlow | null;
  buildPlan: BuildPlan | null;
  guidedBuilderState?: GuidedBuilderState;
}

export function PluginReportProblemDialog({
  open,
  onOpenChange,
  session,
  files,
  logEntries,
  guidedFlow,
  buildPlan,
  guidedBuilderState,
}: PluginReportProblemDialogProps) {
  const { toast } = useToast();
  const [description, setDescription] = useState('');
  const [problemScope, setProblemScope] = useState('1');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedReportId, setSubmittedReportId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleClose = () => {
    setDescription('');
    setProblemScope('1');
    setSubmittedReportId(null);
    setCopied(false);
    onOpenChange(false);
  };

  const handleCopyReportId = async () => {
    if (!submittedReportId) return;
    await navigator.clipboard.writeText(submittedReportId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async () => {
    if (!session || !description.trim()) return;

    setIsSubmitting(true);
    try {
      const trimmedLogEntries =
        logEntries.length > MAX_REPORTED_LOG_ENTRIES
          ? logEntries.slice(-MAX_REPORTED_LOG_ENTRIES)
          : logEntries;

      const result = await api.submitPluginProblemReport(session.sessionId, {
        description: description.trim(),
        problemScope: parseInt(problemScope, 10),
        conversationHistory: session.conversationHistory,
        currentFiles: files,
        currentSettings: session.currentSettings,
        guidedFlow,
        buildPlan,
        logEntries: trimmedLogEntries,
        telemetryState: getDebugState(),
        ...(guidedBuilderState ? { guidedBuilderState } : {}),
      });

      setSubmittedReportId(result.reportId);
    } catch (err) {
      toast({
        title: 'Failed to submit report',
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={submittedReportId ? handleClose : onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        {submittedReportId ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                Report Submitted
              </DialogTitle>
              <DialogDescription>
                Thank you for your feedback. Your report has been recorded.
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <label className="text-sm font-medium">Report ID</label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 rounded bg-muted px-3 py-2 text-sm font-mono select-all">
                  {submittedReportId}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyReportId}
                  className="shrink-0"
                >
                  {copied ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Report a Problem</DialogTitle>
              <DialogDescription>
                Help us improve Fias AI by reporting issues you encounter during building.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label htmlFor="plugin-problem-scope" className="text-sm font-medium">
                  Where did the problem start?
                </label>
                <Select value={problemScope} onValueChange={setProblemScope}>
                  <SelectTrigger id="plugin-problem-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCOPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label htmlFor="plugin-problem-description" className="text-sm font-medium">
                  What went wrong?
                </label>
                <Textarea
                  id="plugin-problem-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))}
                  placeholder="Describe what you expected versus what happened..."
                  rows={5}
                />
                <p className="text-xs text-muted-foreground text-right">
                  {description.length}/{MAX_DESCRIPTION_LENGTH}
                </p>
              </div>

              <p className="text-xs text-muted-foreground">
                This report includes your conversation history, current files, and session metadata
                to help diagnose the issue. No personal data beyond your user ID is included.
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!description.trim() || !session || isSubmitting}
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Submit Report
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
